import { createContext, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { VerifyCodeState } from '@/bo-kit/components/VerifyAccountPage/types';
import { Nullable } from '@/ui-kit/types';
import { useLoginErrorCode } from '@/bo-kit/hooks/useLoginErrorCode';
import { useReturnUrl } from '../../../../../hooks/useReturnUrl';
import {
    LoginAndResetPassErrorCode,
    LoginResponse,
    LoginRequest,
} from '../../../../../transport/types';
import { isLoginResponseVerifyRequiredError } from '../../../../../utils/isLoginResponseVerifyRequiredError';

type UseDoLoginMutation = [
    (data: LoginRequest) => void,
    {
        isLoading: boolean;
        isError: boolean;
        error?: FetchBaseQueryError | SerializedError;
        originalArgs?: LoginRequest;
        data?: LoginResponse;
        isSuccess: boolean;
        reset: () => void;
    }
];

export const UseDoLoginMutationContext = createContext<UseDoLoginMutation | undefined>(undefined);

const useDoLoginMutationFromContext = () => {
    const contextValue = useContext(UseDoLoginMutationContext);
    if (!contextValue) {
        throw new Error(
            "No useLoginMutation defined with react context, pls implement: '<UseDoLoginMutationContext.Provider value={[doLogin, loginState]}>{children}</UseDoLoginMutationContext.Provider>'"
        );
    }
    return contextValue;
};

export const useLogin = () => {
    const { t } = useTranslation('bo-kit__signin');

    const [doLogin, state] = useDoLoginMutationFromContext();

    const { errorCode, roles } = useLoginErrorCode(state);

    const errorMessage = useMemo<string | undefined>(() => {
        if (errorCode === LoginAndResetPassErrorCode.LOGINBLOCKED) {
            return t('Too many attempts, try in few minutes');
        }
        if (errorCode === LoginAndResetPassErrorCode.WRONG_CREDENTIALS) {
            return t('Incorrect e-mail or password');
        }
        if (
            errorCode &&
            [
                LoginAndResetPassErrorCode.NOT_ACTIVATED_USER,
                LoginAndResetPassErrorCode.ROLECONFLICT,
            ].includes(errorCode)
        ) {
            return;
        }
        if (!state.isError) {
            return;
        }
        if (errorCode === LoginAndResetPassErrorCode.NETWORK_ERROR) {
            return t('Network error, check your connection');
        }
        if (
            !state.error ||
            !('error' in state.error) ||
            errorCode === LoginAndResetPassErrorCode.UNKNOWN
        ) {
            return t('An unexpected error occurred');
        }
        return state.error.error ?? null;
    }, [state.error, state.isError, errorCode, t]);

    const [returnUrl] = useReturnUrl();

    // used to verify session's email on login
    const verifyCodeParams = useMemo<Nullable<VerifyCodeState>>(() => {
        if (
            state.error &&
            'data' in state.error &&
            isLoginResponseVerifyRequiredError(state.error.data) &&
            state.originalArgs
        ) {
            return {
                id: state.error.data.data.user_id,
                email: state.originalArgs?.email,
                returnUrl: returnUrl ?? undefined,
                verifyOnLogin: true,
            };
        }
        return null;
    }, [state.error, state.originalArgs, returnUrl]);
    return {
        doLogin,
        ...state,
        isError: errorCode != null,
        error: errorCode,
        errorMessage,
        roles,
        verifyCodeParams,
    };
};
